import { Grid, Paper, Skeleton, Typography } from '@mui/material';
import React, { useMemo } from 'react'
import NumericLabel from 'react-pretty-numbers';
import moment from 'moment';
import NoDataAvailableCard from './NoDataAvailableCard';

const healthCardKeys = ['total_invoices', 'paid_invoices', 'outstanding', 'overdue', 'average_monthly_utilization'];

const ContractHealthOverView = ({ data, loading = {} }) => {
  const health = data?.contract_overview_health;
  const cards = health?.cards;
  const currency = health?.filters?.currency || 'USD';

  const utilizationPeriodLabel = useMemo(() => {
    const { date_from, date_to } = health?.filters || {};
    if (!date_from || !date_to) return '';
    const months = moment(date_to).diff(moment(date_from), 'months');
    return months > 0 ? `over ${months} month${months === 1 ? '' : 's'}` : '';
  }, [health?.filters])

  const renderAmount = (amount) => (
    <NumericLabel params={{ currency: true, currencyIndicator: currency, shortFormat: true, precision: 1, justification: 'L' }}>
      {amount ?? 0}
    </NumericLabel>
  )

  const statCards = [
    {
      key: 'total_invoices',
      label: 'Total Invoices',
      className: 'ca-health-card-neutral',
      value: cards?.total_invoices?.count ?? '--',
      subtext: cards?.total_invoices?.amount != null ? <>{renderAmount(cards.total_invoices.amount)} generated</> : '--',
    },
    {
      key: 'paid_invoices',
      label: 'Paid Invoices',
      className: 'ca-health-card-green',
      value: cards?.paid_invoices?.count ?? '--',
      subtext: cards?.paid_invoices?.percentage != null ? `${cards.paid_invoices.percentage}% by count` : '--',
    },
    {
      key: 'outstanding',
      label: 'Outstanding',
      className: 'ca-health-card-orange',
      value: cards?.outstanding?.amount != null ? renderAmount(cards.outstanding.amount) : '--',
      subtext: cards?.outstanding?.count != null ? `${cards.outstanding.count} open invoice${cards.outstanding.count === 1 ? '' : 's'}` : '--',
    },
    {
      key: 'overdue',
      label: 'Overdue',
      className: 'ca-health-card-red',
      value: cards?.overdue?.amount != null ? renderAmount(cards.overdue.amount) : '--',
      subtext: cards?.overdue?.count != null ? `${cards.overdue.count} invoice${cards.overdue.count === 1 ? '' : 's'} · max ${cards.overdue.max_days_overdue ?? '--'} days` : '--',
    },
    {
      key: 'average_monthly_utilization',
      label: 'Average Monthly Utilization',
      className: 'ca-health-card-green',
      value: cards?.average_monthly_utilization?.average_flight_hours != null ? `${cards.average_monthly_utilization.average_flight_hours} FH` : '--',
      subtext: cards?.average_monthly_utilization?.average_flight_cycles != null
        ? `${cards.average_monthly_utilization.average_flight_cycles} FC${utilizationPeriodLabel ? ` · ${utilizationPeriodLabel}` : ''}`
        : '--',
    },
  ]

  return (
    <div className="contract-analytics-wrapper">
      <div className="ca-page-header">
        <Typography fontSize='15px' fontFamily="Roboto-SemiBold !important">Contract Health Overview <span style={{ fontSize: "12px" }}>Factual indicators, no opaque composite score</span></Typography>
      </div>
      {loading?.contract_overview_health ? (
        <Grid container spacing={1.5} mx={1}>
          {healthCardKeys.map((key) => (
            <Grid size={{ md: 2.4, xs: 12 }} key={key}>
              <Paper className="ca-health-card">
                <Skeleton variant="text" width="55%" height={16} />
                <Skeleton variant="text" width="45%" height={34} sx={{ my: 0.5 }} />
                <Skeleton variant="text" width="75%" height={14} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : !cards ? (
        <NoDataAvailableCard title='No contract health data available' subtext='Try adjusting the date range' />
      ) : (
        <Grid container spacing={1.5} mx={1}>
          {statCards.map((card) => (
            <Grid size={{ md: 2.4, xs: 12 }} key={card.key}>
              <Paper className={`ca-health-card ${card.className}`}>
                <Typography className="ca-health-card-label">{card.label}</Typography>
                <Typography className="ca-health-card-value">{card.value}</Typography>
                <Typography className="ca-health-card-subtext">{card.subtext}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  )
}

export default ContractHealthOverView;